import React, { Component, lazy, useCallback } from 'react'
import _includes from 'lodash/includes'
import _get from 'lodash/get'
import { connect as connectRedux } from 'react-redux'
import { Map, fromJS } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectStaticText } from '../StaticText/connect'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import CommentsLoader from '../CommentsLoader/index'
import LoadMore from '../LoadMore/index'
import OptimizelyFeature from '../OptimizelyFeature/index'
import {
  selectHomeSections,
  sectionStatus,
  selectHomeLoadMoreSections,
  loadMoreSectionsStatus,
} from '../../services/page-manager/index'
import { SCREEN_TYPE_MEMBER_HOME } from '../../services/page-manager/entities/index'
import { scrollToTop } from '../../services/dom/index'
import SectionManager from '../SectionManager'

const config = getConfig()
const memberHomeFirstTouchEnabled = !!_get(config, ['features', 'memberHomeFirstTouch'])
const LoadableMemberHomeFirstTouch = memberHomeFirstTouchEnabled
  ? lazy(() => import('../MemberHomeFirstTouch/index'))
  : null

/**
 * Handles the window's before unload event for Member Home.
 * @description - Before the page is unloaded, this function sets the
 * document body's position to fixed, top, left, and width properties,
 * and then scrolls to the top of the page.
 * @param {undefined} - No parameters are passed to this function.
 * @return {undefined} - This function does not return any value.
 */
function beforeUnload () {
  global.document.body.style.position = 'fixed'
  global.document.body.style.top = '0'
  global.document.body.style.left = '0'
  global.document.body.style.width = '100%'
  scrollToTop()
}

const SectionManagerItem = ({
  removeRowHandler,
  addBackHandler,
  index,
  rowPlacement,
  history,
  sectionType,
  sectionId,
  removedRows,
  prevSectionType,
}) => {
  const onRemoveRow = useCallback(() => {
    removeRowHandler(sectionId)
  }, [removeRowHandler, sectionId])

  const onAddBack = useCallback(() => {
    addBackHandler(sectionId)
  }, [addBackHandler, sectionId])

  return (
    <SectionManager
      index={index}
      rowPlacement={rowPlacement}
      history={history}
      id={sectionId}
      type={sectionType}
      removeRowHandler={onRemoveRow}
      addBackHandler={onAddBack}
      removed={_includes(removedRows, sectionId)}
      screenType={SCREEN_TYPE_MEMBER_HOME}
      prevSectionType={prevSectionType}
    />
  )
}

/**
 * Component to render member home sections
 * @type {React.FunctionComponent}
 */
function MemberHomeSections (props) {
  const {
    sections,
    history,
    removedRows,
    removeRowHandler,
    addBackHandler,
  } = props

  if (sections.status === sectionStatus.EMPTY) {
    return null
  }

  // Convert Immutable List to array FIRST, then map
  // React cannot render Immutable Lists directly
  const itemsArray = sections.items.toArray ? sections.items.toArray() : sections.items

  return itemsArray.map(
    ({ sectionId, rowPlacement, sectionType }, index) => {
      const prevSection = itemsArray[index - 1]
      const prevSectionType = prevSection ? prevSection.sectionType : null

      return (<SectionManagerItem
        key={sectionId}
        index={index}
        rowPlacement={rowPlacement}
        history={history}
        sectionId={sectionId}
        sectionType={sectionType}
        removeRowHandler={removeRowHandler}
        addBackHandler={addBackHandler}
        removedRows={removedRows}
        prevSectionType={prevSectionType}
      />)
    },
  )
}

const LoadMoreSections = (props) => {
  const {
    details: {
      status, processing, currentCount, totalCount, nextSectionId,
    },
    onLoadMore,
  } = props

  if (status === loadMoreSectionsStatus.EMPTY || !nextSectionId) {
    return null
  }

  return (
    <div key="load-more" className="member-home__sections-load_more">
      <LoadMore
        onLoadMore={() => onLoadMore(nextSectionId)}
        buttonClassName={['button--secondary']}
        currentCount={currentCount}
        totalCount={totalCount}
        processing={processing}
        infiniteScroll
        visibilityThreshold={0.01}
      />
    </div>
  )
}

class MemberHome extends Component {
  constructor (props) {
    super(props)
    this.state = {
      removedRows: [],
      previousScreenType: props.screenType,
    }
  }

  componentDidMount () {
    const { props } = this
    const { setTouchDevice } = props
    global.addEventListener(
      'touchstart',
      function onFirstTouch () {
        setTouchDevice(true)
        // we only need to know about one touch, so we can stop listening now
        global.removeEventListener('touchstart', onFirstTouch, false)
      },
      false,
    )

    global.addEventListener('beforeunload', beforeUnload)
  }

  componentWillUnmount () {
    const { clearAllVideosImpressed, clearAllSeriesImpressed } = this.props
    clearAllVideosImpressed()
    clearAllSeriesImpressed()

    global.removeEventListener('beforeunload', beforeUnload)
  }

  onLoadMore = (sectionId) => {
    this.props.fetchSection(sectionId)
  }

  addFirstTouch () {
    if (global.addEventListener) {
      global.addEventListener('touchstart', this.onFirstTouch, false)
    }
  }

  removeFirstTouch () {
    if (global.removeEventListener) {
      global.removeEventListener('touchstart', this.onFirstTouch, false)
    }
  }

  removeRowHandler = (id) => {
    this.props.setFeatureTrackingDataPersistent({
      data: fromJS({ hidePmSectionIds: [id] }),
    })
    const s = new Set(this.state.removedRows)
    s.add(id)
    this.setState({ removedRows: Array.from(s) })
  }

  addBackHandler = (id) => {
    this.props.setFeatureTrackingDataPersistent({
      data: fromJS({ showPmSectionIds: [id] }),
    })
    this.setState((state) => ({
      removedRows: state.removedRows.filter((x) => x !== id),
    }))
  }

  render () {
    const {
      props,
      state,
      removeRowHandler,
      addBackHandler,
      onLoadMore,
    } = this
    const { removedRows } = state
    const { sections, history, loadMoreSections } = props
    return (
      <article className="member-home">
        {LoadableMemberHomeFirstTouch && (
          <>
            <OptimizelyFeature
              flag="member_home__first_touch"
              variation="control"
              isDefault
              anon={false}
              noWrapper
            >
              {null}
            </OptimizelyFeature>
            <OptimizelyFeature
              flag="member_home__first_touch"
              variation="first_touch"
              anon={false}
              noWrapper
            >
              <LoadableMemberHomeFirstTouch />
            </OptimizelyFeature>
          </>
        )}
        <div className="member-home">
          <CommentsLoader />
          <MemberHomeSections
            sections={sections}
            history={history}
            removeRowHandler={removeRowHandler}
            addBackHandler={addBackHandler}
            removedRows={removedRows}
          />
          <LoadMoreSections
            details={loadMoreSections}
            onLoadMore={onLoadMore}
          />
        </div>
      </article>
    )
  }
}

export default compose(
  connectStaticText({ storeKey: 'memberHome' }),
  connectRedux(
    (state) => {
      const {
        user = Map(),
        auth = Map(),
        featureTracking = Map(),
      } = state
      const username = auth.get('username')
      const sections = selectHomeSections(state)
      const loadMoreSections = selectHomeLoadMoreSections(state)

      return {
        username,
        sections,
        featureTracking,
        userStore: user,
        loadMoreSections,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        fetchSection: actions.pageManagerActions.fetchSection,
        setFeatureTrackingDataPersistent:
          actions.featureTracking.setFeatureTrackingDataPersistent,
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        setTouchDevice: actions.page.setPageTouchDevice,
        clearAllVideosImpressed: actions.node.clearAllVideosImpressed,
        clearAllSeriesImpressed: actions.node.clearAllSeriesImpressed,
        renderModal: actions.dialog.renderModal,
        dismissModal: actions.dialog.dismissModal,
        incrementFeatureImpressionCount:
          actions.featureTracking.incrementFeatureImpressionCount,
        resetFeatureImpressionCount:
          actions.featureTracking.resetFeatureImpressionCount,
      }
    },
  ),
)(MemberHome)
